import React, { useState, useEffect, useRef } from 'react';
import { notification } from 'antd';
import CurrencyFormat from 'react-currency-format';
import {
  FileTextOutlined,
  BankOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import lib from '../../../assets/utils/lib';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import './PaymentDrilldown.css';

const STATUS_BRAND = {
  0: { color: '#96650B', icon: <FileTextOutlined /> },
  1: { color: '#3497D3', icon: <BankOutlined /> },
  2: { color: '#722ed1', icon: <DollarOutlined /> },
  3: { color: '#3e9e62', icon: <CheckCircleOutlined /> },
};

function Money({ value }) {
  return (
    <CurrencyFormat
      value={Number(value) || 0}
      displayType="text"
      thousandSeparator
      prefix="₦"
      decimalScale={0}
    />
  );
}

function stageDateLabel(code) {
  switch (Number(code)) {
    case 0: return 'Filed';
    case 1: return 'Forwarded to HM';
    case 2: return 'Forwarded to CFO';
    case 3: return 'Paid';
    default: return 'Stage';
  }
}

function formatShortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Persistent stage tab strip + KPI strip — replaces the standalone Payment Dashboard
 * page. Rendered once at the top of the drilldown, above Months/Facilities/Claims.
 */
const PaymentStageTabs = ({ activeStatus, onSelect }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  useEffect(() => {
    const fetchStatuses = async () => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      setLoading(true);
      try {
        const response = await lib.getPaymentStatuses();
        if (response?.data?.error) {
          notification.error({
            message: 'Error',
            description: response.data.message || 'Failed to fetch payment statuses',
          });
          return;
        }
        const responseData = response?.data || {};
        setStatuses(Array.isArray(responseData.data) ? responseData.data : []);
      } catch (error) {
        console.error('Error fetching payment statuses:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch payment statuses',
        });
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };
    fetchStatuses();
  }, []);

  const activeSummary = statuses.find((s) => s.status_code === activeStatus) || null;

  return (
    <>
      <div className="stage-tabs" role="tablist" aria-label="Payment stage">
        {statuses.map((status) => {
          const brand = STATUS_BRAND[status.status_code] || STATUS_BRAND[0];
          const isActive = status.status_code === activeStatus;
          return (
            <button
              key={status.status_code}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`stage-tab${isActive ? ' is-active' : ''}`}
              style={{ color: isActive ? undefined : brand.color }}
              onClick={() => onSelect?.(status.status_code)}
            >
              <span className="stage-tab__dot" style={{ color: brand.color }} />
              {status.status_label}
              <span className="stage-tab__count">{(status.claims_count || 0).toLocaleString()}</span>
            </button>
          );
        })}
      </div>

      {activeSummary ? (
        <>
          <div className="kpi-strip" aria-label="Stage totals">
            <div className="kpi-tile">
              <div className="kpi-tile__icon" style={{ background: '#eef4ff', color: '#4338ca' }}>▤</div>
              <div>
                <div className="kpi-tile__label">Claims</div>
                <div className="kpi-tile__value">{(activeSummary.claims_count || 0).toLocaleString()}</div>
                <div className="kpi-tile__caption">
                  {Number(activeSummary.manual_items_count || 0).toLocaleString()} manual · {Number(activeSummary.direct_items_count || 0).toLocaleString()} direct
                </div>
              </div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile__icon" style={{ background: '#ecfaf1', color: '#3e9e62' }}>₦</div>
              <div>
                <div className="kpi-tile__label">Claims approved</div>
                <div className="kpi-tile__value"><Money value={getClaimsApprovedTotal(activeSummary)} /></div>
              </div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile__icon" style={{ background: '#fff3e0', color: '#a2540e' }}>↗</div>
              <div>
                <div className="kpi-tile__label">Submitted to PA</div>
                <div className="kpi-tile__value"><Money value={getSubmittedTotal(activeSummary)} /></div>
              </div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile__icon" style={{ background: '#f1e9fb', color: '#722ed1' }}>✓</div>
              <div>
                <div className="kpi-tile__label">PA approved</div>
                <div className="kpi-tile__value"><Money value={getPaApprovedTotal(activeSummary)} /></div>
              </div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile__icon" style={{ background: '#f1f3f7', color: '#5a6675' }}>🏥</div>
              <div>
                <div className="kpi-tile__label">Facilities</div>
                <div className="kpi-tile__value">{(activeSummary.facilities_count || 0).toLocaleString()}</div>
              </div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile__icon" style={{ background: '#f1f3f7', color: '#5a6675' }}>📅</div>
              <div>
                <div className="kpi-tile__label">Months</div>
                <div className="kpi-tile__value">{(activeSummary.months_count || 0).toLocaleString()}</div>
              </div>
            </div>
            {activeSummary.payment_batches_count != null ? (
              <div className="kpi-tile">
                <div className="kpi-tile__icon" style={{ background: '#ecfaf1', color: '#3e9e62' }}>💰</div>
                <div>
                  <div className="kpi-tile__label">Batches</div>
                  <div className="kpi-tile__value">{(activeSummary.payment_batches_count || 0).toLocaleString()}</div>
                </div>
              </div>
            ) : null}
          </div>
          {(activeSummary.earliest_date || activeSummary.latest_date || activeSummary.earliest_stage_date || activeSummary.latest_stage_date) ? (
            <div className="kpi-dates">
              {activeSummary.earliest_date || activeSummary.latest_date ? (
                <span>Encounter {formatShortDate(activeSummary.earliest_date) || '—'} – {formatShortDate(activeSummary.latest_date) || '—'}</span>
              ) : null}
              {activeSummary.earliest_stage_date || activeSummary.latest_stage_date ? (
                <span>{stageDateLabel(activeStatus)} {formatShortDate(activeSummary.earliest_stage_date) || '—'} – {formatShortDate(activeSummary.latest_stage_date) || '—'}</span>
              ) : null}
            </div>
          ) : null}
        </>
      ) : loading ? (
        <div className="kpi-strip" aria-hidden>
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="kpi-tile" key={i} style={{ opacity: 0.5 }}>
              <div className="kpi-tile__icon" style={{ background: '#f1f3f7' }} />
              <div>
                <div className="kpi-tile__label">—</div>
                <div className="kpi-tile__value">—</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
};

export default PaymentStageTabs;
